<script setup>
import { ref, computed } from "vue";
import { accountKisses } from "api";
import Toast from "libs/toast";
import UserType from "./user_type.vue";

defineOptions({
  name: "UserCard",
});

const props = defineProps({
  user: { type: Object, required: true },
});

const I18n = window.I18n;
const locale = I18n.locale;
const tCertified = I18n.t("user.certified");

const kissed = ref(false);
const frenchKissed = ref(false);
const sending = ref(false);
const animation = ref(null);

// Set view-transition-name only at click time, so the cross-doc VT matches on the way out
// (index→show) but not on back (show→index, where Vue cards would be missing/mispositioned
// anyway and cause a flash).
function prepareViewTransition(event) {
  const card = event.currentTarget.closest("user-card");
  if (card) card.style.viewTransitionName = `user-${props.user.id}-avatar`;
}

const approxOnlineStatus = computed(() => {
  const onlineAt = props.user.onlineAt;
  const ageMs = onlineAt ? Date.now() - new Date(onlineAt).getTime() : Infinity;
  if (props.user.online || ageMs < 30 * 60 * 1000)
    return I18n.t("user.online_status.now");
  if (ageMs < 24 * 60 * 60 * 1000) return I18n.t("user.online_status.today");
  if (ageMs < 7 * 24 * 60 * 60 * 1000)
    return I18n.t("user.online_status.recently");
  return null;
});

const approxDistance = computed(() => {
  const distance = props.user.distance;
  if (distance == null) return null;
  if (distance <= 3) return I18n.t("user.geolocation.just_next_to");
  if (distance <= 20) return I18n.t("user.geolocation.very_close");
  if (distance <= 50) return I18n.t("user.geolocation.nearby");
  return null;
});

function seek(userTypeIds, format = "short") {
  const types = I18n.t(`common.user_types.${format}`);
  return userTypeIds.map((id) => types[id]).join(", ").toLowerCase();
}

const openToMessage = computed(() =>
  I18n.t(`user.open_to.${props.user.userTypeId}`, {
    user_types: seek(props.user.seekUserTypes, "plural"),
    defaultValue: null,
  }),
);

const commonFantasies = computed(() => {
  const indexes = props.user.commonFantasyIndexes ?? [];
  const labels = I18n.t("user_detail.fantasy_preferences");
  return indexes.map((i) => {
    const l = labels[i].toLowerCase();
    return l.charAt(0).toUpperCase() + l.slice(1);
  });
});

const kissDisabled = computed(
  () => props.user.isCurrent || kissed.value || sending.value,
);
const frenchKissDisabled = computed(
  () => props.user.isCurrent || frenchKissed.value || sending.value,
);

async function sendKiss(frenchKiss) {
  if (sending.value) return;
  const helper = frenchKiss
    ? accountKisses.frenchKissUser
    : accountKisses.create;
  sending.value = true;
  try {
    await helper({ params: { id: props.user.id } });
    if (frenchKiss) frenchKissed.value = true;
    else kissed.value = true;
    animation.value = { frenchKiss, key: Date.now() };
  } catch (e) {
    if (e.response?.status === 402 && e.body?.redirectTo) {
      window.location.assign(e.body.redirectTo);
      return;
    }
    new Toast(e.body?.error || "Une erreur est survenue.", {
      type: "alert",
    }).show();
  } finally {
    sending.value = false;
  }
}

function onAnimationEnd(event) {
  if (event.animationName === "kissAnimationRemover") animation.value = null;
}
</script>

<template>
  <user-card
    data-controller="history"
    class="user-card"
    :class="user.hasFrenchKiss ? 'has-french-kiss' : null"
  >
    <a
      class="ios:disable-touch-context"
      :href="user.path"
      data-action="history#addAnchorToCurrentState"
      :data-anchor="`si-${user.id}`"
      @click="prepareViewTransition"
    >
      <header></header>

      <img
        v-if="user.avatarUrl"
        :src="user.avatarUrl"
        alt=""
        width="410"
        height="546"
        class="ios:disable-touch-context"
      />
      <user-card-placeholder v-else class="flex h-full w-full">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="171"
          height="101"
          class="mx-auto"
          viewBox="0 0 171 101"
          fill="none"
        >
          <path
            d="M112.685 1.24163C109.986 -0.567098 106.419 -0.38456 103.919 1.69021L89.9789 13.259C87.2624 15.5134 83.3251 15.5125 80.6097 13.2567L66.724 1.7214C64.2255 -0.354184 60.6593 -0.538875 57.9597 1.2675L2.65152 38.2765C-0.23913 40.2108 -0.871994 44.1962 1.27727 46.9308C28.781 82.0828 56.9929 100.139 85.2934 100.859C113.594 100.139 141.806 82.0528 169.309 46.9308C171.459 44.1955 170.828 40.2094 167.938 38.2726L112.685 1.24163Z"
            fill="#B9A7A8"
          />
        </svg>
      </user-card-placeholder>

      <french-kiss-badge v-if="user.hasFrenchKiss" :class="`locale-${locale}`">
        <svg viewBox="0 0 80 80">
          <defs>
            <path
              :id="`circlePath-${user.id}`"
              d="M 40, 40 m -30, 0 a 30,30 0 1,1 60,0 a 30,30 0 1,1 -60,0"
            />
          </defs>
          <text text-anchor="middle">
            <textPath
              class="received"
              :href="`#circlePath-${user.id}`"
              startOffset="50%"
            >
              {{ I18n.t("french_kiss.received.long") }}
            </textPath>
            <textPath
              class="sent"
              :href="`#circlePath-${user.id}`"
              startOffset="50%"
            >
              {{ I18n.t("french_kiss.sent.short") }}
            </textPath>
          </text>
        </svg>
      </french-kiss-badge>
    </a>

    <kiss-animation
      v-if="animation"
      :key="animation.key"
      :class="{ 'is-french-kiss': animation.frenchKiss }"
      @animationend="onAnimationEnd"
    >
      <tag-wrapper>
        <tag-kiss class="tag is-medium">
          <font-icon
            :class="animation.frenchKiss ? 'french-kiss' : 'kiss--fill'"
          ></font-icon>
          {{
            animation.frenchKiss
              ? I18n.t("french_kiss.sent.short")
              : I18n.t("kiss.sent_short")
          }}
        </tag-kiss>
        <tag-gradient></tag-gradient>
      </tag-wrapper>
    </kiss-animation>

    <footer-user-card>
      <footer-content>
        <a
          :href="user.path"
          data-action="history#addAnchorToCurrentState"
          :data-anchor="`si-${user.id}`"
          class="block no-underline"
          @click="prepareViewTransition"
        >
          <div class="grid gap-1">
            <span
              v-if="approxOnlineStatus"
              class="inline-flex items-center gap-1 py-0.5"
            >
              <span class="flex justify-center items-center h-3 w-3 p-1">
                <span class="h-2 w-2 bg-online rounded-full"></span>
              </span>
              <span class="text-small text-brand-050">{{
                approxOnlineStatus
              }}</span>
            </span>
            <span
                v-if="props.user.isAvailable"
                class="inline-flex items-center gap-1 py-0.5 text-small text-brand-050"
            >
              <font-icon class="speed_sex--fill"></font-icon>
              <span>{{I18n.t("user.available_tonight.name")}}</span>
            </span>

            <span class="block">
              <em class="title-big frozen:text-brand-lightest">{{
                user.username
              }}</em>
              <span class="text-super-big text-opacity-light-70 ml-1">{{
                user.age
              }}</span>
            </span>

            <span class="flex gap-1">
              <span class="tag is-on-picture is-light is-x-small capitalize"
                ><UserType :type="user.userTypeId" :size="12" />
                {{ I18n.t("common.user_types.long")[user.userTypeId] }}</span
              >
              <span v-if="user.certified" class="tag is-on-picture is-light is-x-small"
                ><font-icon class="certification--fill"></font-icon>
                {{ tCertified }}</span
              >
              <span v-if="approxDistance" class="tag is-on-picture is-light is-x-small"
                ><font-icon class="geolocation--fill"></font-icon>
                {{ approxDistance }}</span
              >
            </span>

            <span class="grid gap-0.5 text-small text-brand-050">
              <span>{{ openToMessage }}</span>
              <span v-if="commonFantasies.length">
                <strong>{{ I18n.t("common.in_common") }} : </strong
                >{{ commonFantasies.join(", ") }}
              </span>
            </span>
          </div>
        </a>

        <user-card-actions>
          <button
            type="button"
            class="icon-button is-french-kiss has-drop-shadow"
            :disabled="frenchKissDisabled"
            @click="sendKiss(true)"
          >
            <font-icon class="french-kiss"></font-icon>
          </button>
          <button
            type="button"
            class="icon-button has-drop-shadow"
            :disabled="kissDisabled"
            @click="sendKiss(false)"
          >
            <font-icon class="kiss--fill"></font-icon>
          </button>
        </user-card-actions>
      </footer-content>
    </footer-user-card>
  </user-card>
</template>

<style scoped>
.user-card {
  height: 100%;
  aspect-ratio: auto;

  & > a {
    aspect-ratio: auto;
    height: 100%;

    & > img {
      aspect-ratio: auto;
      height: 100%;
    }
  }
}
</style>
